// src/tattoo/lib/studioPhotos.js
//
// Helpers for the studio gallery feature. Files live in the Supabase
// Storage bucket named `studio-photos`; metadata lives in the
// public.studio_photos table (see supabase/migrations/20260430000000).
//
// Path convention inside the bucket: `{shop_id}/{uuid}.{ext}`.
// The leading shop_id segment is required by the bucket's RLS policies
// so they can verify ownership.

import { supabase } from "./supabaseClient";

const BUCKET = "studio-photos";

/**
 * List a shop's gallery photos in display order. Returns an array of
 * { id, shop_id, storage_path, caption, display_order, created_at, url }.
 * `url` is the rendered public URL for direct use in <img src>.
 *
 * Falls back to an empty array on error (logged) so callers don't have
 * to guard the gallery render path.
 */
export async function listStudioPhotos(shopId) {
  if (!shopId) return [];

  try {
    const { data, error } = await supabase
      .from("studio_photos")
      .select("id, shop_id, storage_path, caption, display_order, is_featured, created_at")
      .eq("shop_id", shopId)
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("[studioPhotos] list failed", error);
      return [];
    }

    return (data || []).map((row) => ({
      ...row,
      url: publicUrlFor(row.storage_path),
    }));
  } catch (e) {
    console.warn("[studioPhotos] list threw", e);
    return [];
  }
}

/**
 * Upload a file to the studio-photos bucket and create a metadata row.
 * Returns the inserted row (with `.url` resolved) on success, throws on
 * failure so the management UI can surface the error.
 *
 * file: a File object from an <input type="file"> or drop event.
 * shopId: the owner shop's UUID.
 * opts.caption?: optional caption.
 * opts.displayOrder?: defaults to current max + 1 if not provided.
 */
export async function uploadStudioPhoto(shopId, file, opts = {}) {
  if (!shopId) throw new Error("shopId required");
  if (!file) throw new Error("file required");

  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  // Use crypto.randomUUID when available, fall back to a timestamp + random
  // segment for environments without it (older Safari).
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const storagePath = `${shopId}/${id}.${ext}`;

  // 1. Upload the file
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, {
      cacheControl: "31536000",
      upsert: false,
      contentType: file.type || undefined,
    });

  if (upErr) {
    console.error("[studioPhotos] upload failed", upErr);
    throw upErr;
  }

  // 2. Create the metadata row. If this fails, attempt to clean up the
  // orphaned storage object so we don't leak space.
  const insertPayload = {
    shop_id: shopId,
    storage_path: storagePath,
    caption: opts.caption || null,
  };
  if (typeof opts.displayOrder === "number") {
    insertPayload.display_order = opts.displayOrder;
  }

  const { data, error: dbErr } = await supabase
    .from("studio_photos")
    .insert(insertPayload)
    .select("id, shop_id, storage_path, caption, display_order, is_featured, created_at")
    .single();

  if (dbErr || !data) {
    console.error("[studioPhotos] metadata insert failed", dbErr);
    // Best-effort cleanup of the orphaned storage object.
    try {
      await supabase.storage.from(BUCKET).remove([storagePath]);
    } catch (cleanupErr) {
      console.warn(
        "[studioPhotos] orphan cleanup failed",
        storagePath,
        cleanupErr
      );
    }
    throw dbErr || new Error("Could not save photo metadata");
  }

  return { ...data, url: publicUrlFor(data.storage_path) };
}

/**
 * Delete a photo (metadata row + storage object). Best-effort on the
 * storage delete — if the row is gone but the object remains, the orphan
 * just consumes a bit of bucket quota; if we surfaced the error the user
 * would be told their delete failed when their photo is already gone.
 */
export async function deleteStudioPhoto(photoId) {
  if (!photoId) throw new Error("photoId required");

  // Look up storage_path first so we can clean up the file after.
  const { data: row, error: getErr } = await supabase
    .from("studio_photos")
    .select("storage_path")
    .eq("id", photoId)
    .maybeSingle();

  if (getErr) {
    console.error("[studioPhotos] lookup before delete failed", getErr);
    throw getErr;
  }

  const { error: delErr } = await supabase
    .from("studio_photos")
    .delete()
    .eq("id", photoId);

  if (delErr) {
    console.error("[studioPhotos] delete failed", delErr);
    throw delErr;
  }

  if (row?.storage_path) {
    try {
      await supabase.storage.from(BUCKET).remove([row.storage_path]);
    } catch (e) {
      console.warn(
        "[studioPhotos] storage cleanup after row delete failed",
        row.storage_path,
        e
      );
    }
  }
}

/**
 * Mark a studio photo as the shop's featured hero. Clears any
 * previously-featured photo first to satisfy the
 * studio_photos_one_featured_per_shop_idx unique partial index (which
 * would otherwise reject the second `is_featured = true` row).
 *
 * Returns the newly-featured row (with `.url` resolved). Mirrors the
 * setFlashFeatured pattern in ../lib/flash.js so consumers can treat
 * both toggles identically.
 */
export async function setStudioPhotoFeatured(id) {
  if (!id) throw new Error("id required");

  // Look up shop_id + current state so we can no-op if it's already
  // featured (avoids a needless clear-others write).
  const { data: target, error: getErr } = await supabase
    .from("studio_photos")
    .select("id, shop_id, is_featured, storage_path, caption, display_order, created_at")
    .eq("id", id)
    .maybeSingle();
  if (getErr || !target) {
    console.error("[studioPhotos] setFeatured lookup failed", getErr);
    throw getErr || new Error("Could not load photo to feature");
  }
  if (target.is_featured) {
    return { ...target, url: publicUrlFor(target.storage_path) };
  }

  // 1. Clear any other featured photo on this shop.
  const { error: clearErr } = await supabase
    .from("studio_photos")
    .update({ is_featured: false })
    .eq("shop_id", target.shop_id)
    .eq("is_featured", true);
  if (clearErr) {
    console.error("[studioPhotos] setFeatured clear-others failed", clearErr);
    throw clearErr;
  }

  // 2. Set this one as featured.
  const { data, error: setErr } = await supabase
    .from("studio_photos")
    .update({ is_featured: true })
    .eq("id", id)
    .select("id, shop_id, storage_path, caption, display_order, is_featured, created_at")
    .single();
  if (setErr || !data) {
    console.error("[studioPhotos] setFeatured update failed", setErr);
    throw setErr || new Error("Could not mark as featured");
  }
  return { ...data, url: publicUrlFor(data.storage_path) };
}

/**
 * Clear the featured flag on a studio photo. The public page will fall
 * back to whatever the next hero source is (see the precedence chain
 * in ArtistPublicPage.jsx).
 */
export async function unsetStudioPhotoFeatured(id) {
  if (!id) throw new Error("id required");
  const { data, error } = await supabase
    .from("studio_photos")
    .update({ is_featured: false })
    .eq("id", id)
    .select("id, shop_id, storage_path, caption, display_order, is_featured, created_at")
    .single();
  if (error || !data) {
    console.error("[studioPhotos] unsetFeatured failed", error);
    throw error || new Error("Could not clear featured");
  }
  return { ...data, url: publicUrlFor(data.storage_path) };
}

function publicUrlFor(storagePath) {
  if (!storagePath) return null;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return data?.publicUrl || null;
}
